<template>
    <modal-form
        editTitle="Portal-Aufgabe bearbeiten"
        createTitle="Neue Portal-Aufgabe"
        v-bind:isLoading="isLoading"
    >
        <table>
            <tr>
                <th><label for="templateName" class="label">Aufgabe:</label></th>
                <td>
                    <todo-name-select id="templateName" v-model="form.name" />
                </td>
            </tr>

            <tr>
                <th><label for="templateDescription" class="label">Beschreibung:</label></th>
                <td>
                    <textarea
                        id="templateDescription"
                        class="textarea"
                        rows="2"
                        v-model="form.description"
                    ></textarea>
                </td>
            </tr>

            <tr>
                <th><label class="label">Fälligkeit:</label></th>
                <td>
                    <toggle-switch v-model="form.dueDateMode">
                        <option v-for="mode in dueDateModeOptions" :key="mode.key" :value="mode.key">
                            {{ mode.value }}
                        </option>
                    </toggle-switch>
                    <p class="help" v-if="form.dueDateMode === 'none'">
                        Ohne Datum entsteht ein reiner Checklisten-Punkt — der Kunde setzt
                        das Datum selbst.
                    </p>
                </td>
            </tr>

            <tr v-if="form.dueDateMode !== 'none'">
                <th><label for="templateDays" class="label">{{ daysLabel }}:</label></th>
                <td>
                    <input
                        type="number"
                        min="0"
                        id="templateDays"
                        class="input"
                        v-model.number="form.daysOffset"
                    >
                    <p class="help" v-if="form.dueDateMode === 'before_start'">
                        Reisen ohne Startdatum bekommen die Aufgabe ohne Frist.
                    </p>
                </td>
            </tr>

            <tr>
                <th><label class="label">Zuständig:</label></th>
                <td>
                    <toggle-switch v-model="form.autoAssignToCreator">
                        <option :value="true">Ersteller der Reise</option>
                        <option :value="false">niemand</option>
                    </toggle-switch>
                </td>
            </tr>

            <tr>
                <th><label for="templateSort" class="label">Reihenfolge:</label></th>
                <td>
                    <input type="number" id="templateSort" class="input" v-model.number="form.sortOrder">
                    <p class="help">In 10er-Schritten vergeben, dann bleibt Platz zum Einsortieren.</p>
                </td>
            </tr>

            <tr>
                <th><label class="label">Aktiv:</label></th>
                <td>
                    <toggle-switch v-model="form.active">
                        <option :value="true">Ja</option>
                        <option :value="false">Nein</option>
                    </toggle-switch>
                </td>
            </tr>

            <tr>
                <th><label class="label">Hinzufügen:</label></th>
                <td>
                    <toggle-switch v-model="addToExistingOrders">
                        <option :value="false">nur in neue Reisen</option>
                        <option :value="true">auch in bestehende Reisen</option>
                    </toggle-switch>
                    <p class="help">
                        Bestehende Reisen: alle im Portal sichtbaren Reisen dieses Kunden,
                        die noch nicht begonnen haben. Mehrfaches Hinzufügen erzeugt keine
                        Duplikate.
                    </p>
                </td>
            </tr>
        </table>
    </modal-form>
</template>

<script>
import TabForm from '../form';
import { ToggleSwitch } from '@components/form';
import TodoNameSelect from '@root/pages/clientPortal/todos/TodoNameSelect';
import { getAxios } from '@api';
import { notifySuccess, notifyError } from '@components/Notification';
import { PORTAL_TODO_DUE_MODE_OPTIONS } from './options';

/**
 * Create/edit a predefined ClientPortal task.
 *
 * Named TodoTemplateForm rather than Form.vue — that name is already taken in this
 * folder by the Portal-Felder form. The "auch in bestehende Reisen" toggle mirrors
 * the provider/agency template form (Tabs/Todos/Form.vue): without it a template
 * would only ever apply to trips created after it was defined.
 */
export default {
    mixins: [TabForm],

    property: 'portalTodoTemplates',

    form: {
        name: '',
        description: '',
        dueDateMode: 'before_start',
        daysOffset: 14,
        sortOrder: 0,
        active: true,
        autoAssignToCreator: true,
    },

    data: function () {
        return {
            dueDateModeOptions: PORTAL_TODO_DUE_MODE_OPTIONS,
            addToExistingOrders: false,
        };
    },

    computed: {
        daysLabel: function () {
            return this.form.dueDateMode === 'before_start'
                ? 'Tage vor Reisebeginn'
                : 'Tage nach Anlage der Reise';
        },
    },

    methods: {
        handleFormSubmit: function () {
            this.isLoading = true;

            // A checklist template carries no offset; sending a stale one would be
            // saved and then reappear if the mode is switched back.
            if (this.form.dueDateMode === 'none') {
                this.form.daysOffset = null;
            }

            this.$store.dispatch('updateProperty', {
                key: this.$options.property,
                data: this.form,
            }).then(
                (data) => {
                    this.visible = false;
                    notifySuccess('Das Element wurde erfolgreich hinzugefügt!');
                    this.$emit('success', { form: this.form, data });
                    this.isLoading = false;

                    if (this.addToExistingOrders) {
                        this.applyToExistingOrders(data);
                    }
                },
                () => {
                    this.isLoading = false;
                    notifyError('Das Element konnte nicht hinzugefügt werden! Server Error!');
                }
            );
        },

        // On create the new id is only known from the refreshed collection, so look
        // it up by name like the provider/agency template form does.
        applyToExistingOrders: function (data) {
            const templateId = this.form.id
                || (data.portalTodoTemplates || [])
                    .filter(item => item.name === this.form.name)
                    .map(item => item.id)
                    .pop();

            if (!templateId) {
                notifyError('Die Aufgabe konnte den bestehenden Reisen nicht zugeordnet werden.');
                return;
            }

            getAxios('portal_todo_templates/' + templateId + '/add_to_orders').then(
                () => notifySuccess('Die Aufgabe wurde zu den bestehenden Reisen hinzugefügt.'),
                () => notifyError('Die Aufgabe konnte nicht zu den bestehenden Reisen hinzugefügt werden.')
            );
        },
    },

    components: {
        ToggleSwitch,
    },
};
</script>
